import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { ContactFilterCriteriaProperty, ImpersonationBroker, type IContact, asApiError } from '../../../extViewmodels';
import { Direction, type IConversation } from '../../../extViewmodels/TextMessagingViewModel';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../models/NameUtils';
import type { ILeadSearchView } from '../../viewModels/leads/interfaces';
import { queryKeys } from './queryKeys';

const CONTACTS_STALE_TIME_MS = 5 * 60_000;
/** Number searches run one request each, so only the newest unlinked texters get one. */
const MAX_PHONE_LOOKUPS = 10;

export type TextInboxItem = {
	conversationId: string;
	/** Changes whenever a new message lands, so it doubles as the "new text" signal. */
	latestMessageKey: string;
	contactId: string | undefined;
	contactName: string;
	companyId: string | undefined;
	companyName: string | undefined;
	e164: string | undefined;
	phoneStandard: string | undefined;
	snippet: string;
	receivedAt: string | undefined;
	unreadCount: number;
	/** Other contacts sharing the number when the lead was guessed from it; 0 for linked conversations. */
	otherContactCount: number;
};

export const formatOtherContacts = (count: number) =>
	count > 0 ? `+${count} other contact${count === 1 ? '' : 's'}` : undefined;

/** A lead found by searching an unlinked conversation's number. */
export type TextInboxPhoneMatch = {
	contactId: string;
	contactName: string;
	companyId: string;
	companyName: string | undefined;
	otherContactCount: number;
};

export function toPhoneMatch(views: readonly ILeadSearchView[]): TextInboxPhoneMatch | undefined {
	const matches = views.flatMap(view => {
		const contact = view.contactReferences?.[0];
		return contact?.id && view.company?.id ? [{ contact, company: view.company }] : [];
	});
	const contactIds = new Set(matches.map(match => match.contact.id));
	const [first] = matches;
	if (!first?.contact.id || !first.company.id) return undefined;
	return {
		contactId: first.contact.id,
		contactName: getDisplayName(first.contact),
		companyId: first.company.id,
		companyName: first.company.companyName,
		otherContactCount: contactIds.size - 1,
	};
}

export function toTextInboxItems(
	conversations: readonly IConversation[],
	contactsById: ReadonlyMap<string, IContact>,
	phoneMatchesByE164: ReadonlyMap<string, TextInboxPhoneMatch> = new Map()
): TextInboxItem[] {
	return conversations
		.filter(conversation => conversation.unreadMessageCount > 0)
		.map(conversation => {
			const recipient = conversation.toNumbers?.[0];
			const contactId = recipient?.contact?.id;
			const contact = contactId ? contactsById.get(contactId) : undefined;
			const lastMessage = conversation.lastMessage;
			const lastInbound = lastMessage?.direction === Direction.Inbound ? lastMessage : undefined;
			const name = getDisplayName(contact ?? recipient?.contact ?? {});
			const e164 = recipient?.number?.e164;
			const phoneMatch = !contactId && e164 ? phoneMatchesByE164.get(e164) : undefined;
			return {
				conversationId: conversation.id,
				latestMessageKey: `${conversation.id}:${lastMessage?.id ?? conversation.unreadMessageCount}`,
				contactId: contactId ?? phoneMatch?.contactId,
				contactName: name || phoneMatch?.contactName || recipient?.number?.standard || 'Unknown number',
				companyId: contact?.companyId ?? contact?.company?.id ?? phoneMatch?.companyId,
				companyName: contact?.companyName ?? contact?.company?.companyName ?? phoneMatch?.companyName,
				e164,
				phoneStandard: recipient?.number?.standard,
				snippet: lastInbound?.text || (lastInbound?.media?.length ? 'Sent an attachment' : ''),
				receivedAt: lastInbound?.sentDate ?? lastInbound?.creationDate ?? conversation.earliestUnreadMessageDate,
				unreadCount: conversation.unreadMessageCount,
				otherContactCount: phoneMatch?.otherContactCount ?? 0,
			};
		})
		.sort((a, b) => (b.receivedAt ?? '').localeCompare(a.receivedAt ?? ''));
}

export type TextInbox = {
	items: TextInboxItem[];
	/** False until the conversations have loaded and every texter's lead lookup has settled. */
	settled: boolean;
};

/**
 * Turns the rep's unread conversations into rows that can open the right lead.
 * Conversations only carry a contact reference, so the contacts are fetched in
 * one batch to learn which company (lead) each texter belongs to. The API links a
 * conversation only when exactly one contact has the number, so unlinked ones are
 * looked up by number and shown with the first lead found.
 */
export function useTextInbox(conversations: readonly IConversation[] | undefined): TextInbox {
	const userSession = useUserSession();
	const contactIds = [
		...new Set(
			(conversations ?? []).flatMap(conversation => {
				const id = conversation.toNumbers?.[0]?.contact?.id;
				return conversation.unreadMessageCount > 0 && id ? [id] : [];
			})
		),
	].sort();

	const contactsQuery = useQuery<ReadonlyMap<string, IContact>, Error>({
		queryKey: queryKeys.focusedView.textInboxContacts(contactIds),
		enabled: contactIds.length > 0,
		staleTime: CONTACTS_STALE_TIME_MS,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IContact[]>(
				ImpersonationBroker.composeApiUrl({ urlPath: 'contact/byIds' }),
				'POST',
				contactIds
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return new Map((result.value ?? []).map(contact => [contact.id, contact]));
		},
		meta: { errorLogging: { category: 'FocusedViewTexting', action: 'TextInboxContactsQuery' } },
	});

	const contactsById = contactsQuery.data ?? new Map<string, IContact>();
	const unlinkedE164s = [
		...new Set(
			toTextInboxItems(conversations ?? [], contactsById).flatMap(item =>
				!item.contactId && item.e164 ? [item.e164] : []
			)
		),
	].slice(0, MAX_PHONE_LOOKUPS);
	const phoneMatchQueries = useQueries({
		queries: unlinkedE164s.map(e164 => ({
			queryKey: queryKeys.focusedView.textInboxPhoneMatch(e164),
			staleTime: CONTACTS_STALE_TIME_MS,
			retry: false,
			queryFn: async () => {
				const result = await userSession.webServiceHelper.callWebServiceAsync<ILeadSearchView[]>(
					ImpersonationBroker.composeApiUrl({ urlPath: 'lead/filter/byContact' }),
					'POST',
					{ criteria: [{ property: ContactFilterCriteriaProperty.PhoneNumber, value: e164 }] }
				);
				if (!result.success) {
					throw asApiError(result);
				}
				return toPhoneMatch(result.value ?? []) ?? null;
			},
			meta: { errorLogging: { category: 'FocusedViewTexting', action: 'TextInboxPhoneMatchQuery' } },
		})),
	});
	const phoneMatchesByE164 = new Map<string, TextInboxPhoneMatch>();
	unlinkedE164s.forEach((e164, index) => {
		const match = phoneMatchQueries[index]?.data;
		if (match) phoneMatchesByE164.set(e164, match);
	});
	const phoneMatchesSettled = phoneMatchQueries.every(query => query.isSuccess || query.isError);

	const contactsSettled =
		contactIds.length === 0 || ((contactsQuery.isSuccess || contactsQuery.isError) && !contactsQuery.isPlaceholderData);
	return {
		items: toTextInboxItems(conversations ?? [], contactsById, phoneMatchesByE164),
		settled: conversations !== undefined && contactsSettled && phoneMatchesSettled,
	};
}
